import {
  AppChangelogService,
  ClockStationFirmwareService,
  DeviceFirmwareService,
  EspFirmwareService,
  OpenAPI as ArtifactsOpenAPI,
} from "../api/artifacts";
import type {
  AppPlatform,
  ContourType,
  CreateAppChangelogRequest,
  DeployRequest,
  DeviceType,
  UpdateAppChangelogRequest,
  UpdateClockStationFirmwareRequest,
  UpdateDeviceFirmwareRequest,
  UpdateEspFirmwareRequest,
  UploadClockStationFirmwareRequest,
  UploadDeviceFirmwareRequest,
  UploadEspFirmwareRequest,
} from "../api/artifacts";

const getArtifactsToken = async () => {
  if (!ArtifactsOpenAPI.TOKEN) return "";

  if (typeof ArtifactsOpenAPI.TOKEN === "string") {
    return ArtifactsOpenAPI.TOKEN;
  }

  return ArtifactsOpenAPI.TOKEN({
    method: "GET",
    url: "",
  });
};

const downloadArtifactFile = async (url: string) => {
  const token = await getArtifactsToken();
  const headers = new Headers({
    Accept: "application/octet-stream",
  });

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(`${ArtifactsOpenAPI.BASE}${url}`, {
    method: "GET",
    headers,
  });

  if (!response.ok) {
    throw new Error(`Ошибка скачивания файла: ${response.status}`);
  }

  return response.blob();
};

const putArtifactRequest = async (
  url: string,
  body: BodyInit,
  options?: { contentType?: string }
) => {
  const token = await getArtifactsToken();
  const headers = new Headers({
    Accept: "application/json",
  });

  if (options?.contentType) {
    headers.set("Content-Type", options.contentType);
  }

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(`${ArtifactsOpenAPI.BASE}${url}`, {
    method: "PUT",
    headers,
    body,
  });

  if (!response.ok) {
    throw new Error(`Ошибка загрузки файла: ${response.status}`);
  }

  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    return response.json();
  }

  return response.text();
};

const putArtifactFormData = async (url: string, formData: Record<string, string | Blob>) => {
  const body = new FormData();

  Object.entries(formData).forEach(([key, value]) => {
    body.append(key, value);
  });

  return putArtifactRequest(url, body);
};

const postArtifactJson = async (url: string, body: unknown) => {
  const token = await getArtifactsToken();
  const headers = new Headers({
    Accept: "application/json",
    "Content-Type": "application/json",
  });

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(`${ArtifactsOpenAPI.BASE}${url}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`Ошибка запроса: ${response.status}`);
  }

  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    return response.json();
  }

  return response.text();
};

export const ArtifactsApi = {
  appChangelogs: {
    create: async (payload: CreateAppChangelogRequest) => {
      return AppChangelogService.postApiAppChangelogs(payload);
    },

    getList: async (page = 1, pageSize = 20, search?: string, platform?: AppPlatform) => {
      return AppChangelogService.getApiAppChangelogs(page, pageSize, search, platform);
    },

    update: async (id: string, payload: UpdateAppChangelogRequest) => {
      return AppChangelogService.putApiAppChangelogs(id, payload);
    },

    getById: async (id: string) => {
      return AppChangelogService.getApiAppChangelogs1(id);
    },

    delete: async (id: string) => {
      return AppChangelogService.deleteApiAppChangelogs(id);
    },

    getVersions: async (platform?: AppPlatform) => {
      return AppChangelogService.getApiAppChangelogsVersions(platform);
    },

    getHistory: async (id: string) => {
      return AppChangelogService.getApiAppChangelogsHistory(id);
    },
  },

  deviceFirmwares: {
    create: async (payload?: UploadDeviceFirmwareRequest) => {
      return DeviceFirmwareService.postApiDeviceFirmwares(payload);
    },

    getList: async (page = 1, pageSize = 20, search?: string, deviceType?: DeviceType) => {
      return DeviceFirmwareService.getApiDeviceFirmwares(page, pageSize, search, deviceType);
    },

    parse: async (file: Blob) => {
      return DeviceFirmwareService.postApiDeviceFirmwaresParse({file});
    },

    update: async (id: string, payload: UpdateDeviceFirmwareRequest) => {
      return DeviceFirmwareService.putApiDeviceFirmwares(id, payload);
    },

    getById: async (id: string) => {
      return DeviceFirmwareService.getApiDeviceFirmwares1(id);
    },

    delete: async (id: string) => {
      return DeviceFirmwareService.deleteApiDeviceFirmwares(id);
    },

    updateFile: async (id: string, file: Blob) => {
      return DeviceFirmwareService.putApiDeviceFirmwaresFile(id, file);
    },

    deleteFile: async (id: string) => {
      return DeviceFirmwareService.deleteApiDeviceFirmwaresFile(id);
    },

    deploy: async (id: string, payload: DeployRequest) => {
      return postArtifactJson(`/api/device-firmwares/${encodeURIComponent(id)}/deploy`, payload);
    },

    removeFromContour: async (id: string, payload: DeployRequest) => {
      return postArtifactJson(`/api/device-firmwares/${encodeURIComponent(id)}/remove-from-contour`, payload);
    },

    getVersions: async (deviceType?: DeviceType) => {
      return DeviceFirmwareService.getApiDeviceFirmwaresVersions(deviceType);
    },

    getHistory: async (id: string) => {
      return DeviceFirmwareService.getApiDeviceFirmwaresHistory(id);
    },

    download: async (id: string) => {
      return downloadArtifactFile(`/api/device-firmwares/${encodeURIComponent(id)}/download`);
    },
  },

  clockStationFirmwares: {
    create: async (payload?: UploadClockStationFirmwareRequest) => {
      return ClockStationFirmwareService.postApiClockStationFirmwares(payload);
    },

    getList: async (page = 1, pageSize = 20, search?: string) => {
      return ClockStationFirmwareService.getApiClockStationFirmwares(page, pageSize, search);
    },

    parse: async (file: Blob) => {
      return ClockStationFirmwareService.postApiClockStationFirmwaresParse({file});
    },

    update: async (id: string, payload: UpdateClockStationFirmwareRequest) => {
      return ClockStationFirmwareService.putApiClockStationFirmwares(id, payload);
    },

    getById: async (id: string) => {
      return ClockStationFirmwareService.getApiClockStationFirmwares1(id);
    },

    delete: async (id: string) => {
      return ClockStationFirmwareService.deleteApiClockStationFirmwares(id);
    },

    updateFile: async (id: string, file: Blob) => {
      return putArtifactFormData(`/api/clock-station-firmwares/${encodeURIComponent(id)}/file`, {file});
    },

    deleteFile: async (id: string) => {
      return ClockStationFirmwareService.deleteApiClockStationFirmwaresFile(id);
    },

    getVersions: async () => {
      return ClockStationFirmwareService.getApiClockStationFirmwaresVersions();
    },

    getHistory: async (id: string) => {
      return ClockStationFirmwareService.getApiClockStationFirmwaresHistory(id);
    },

    download: async (id: string) => {
      return downloadArtifactFile(`/api/clock-station-firmwares/${encodeURIComponent(id)}/download`);
    },
  },

  espFirmwares: {
    getCurrent: (espType: string, currentVersion: string, contour: ContourType) => {
      return EspFirmwareService.getApiEspFirmwaresCurrent(espType, currentVersion, contour);
    },

    create: async (payload?: UploadEspFirmwareRequest) => {
      return EspFirmwareService.postApiEspFirmwares(payload);
    },

    getList: async (page = 1, pageSize = 20, search?: string) => {
      return EspFirmwareService.getApiEspFirmwares(page, pageSize, search);
    },

    parse: async (file: Blob) => {
      return EspFirmwareService.postApiEspFirmwaresParse({file});
    },

    update: async (id: string, payload: UpdateEspFirmwareRequest) => {
      return EspFirmwareService.putApiEspFirmwares(id, payload);
    },

    getById: async (id: string) => {
      return EspFirmwareService.getApiEspFirmwares1(id);
    },

    delete: async (id: string) => {
      return EspFirmwareService.deleteApiEspFirmwares(id);
    },

    updateFile: async (id: string, file: Blob) => {
      return EspFirmwareService.putApiEspFirmwaresFile(id, {file});
    },

    deleteFile: async (id: string) => {
      return EspFirmwareService.deleteApiEspFirmwaresFile(id);
    },

    deploy: async (id: string, payload: DeployRequest) => {
      return EspFirmwareService.postApiEspFirmwaresDeploy(id, payload);
    },

    removeFromContour: async (id: string, payload: DeployRequest) => {
      return EspFirmwareService.postApiEspFirmwaresRemoveFromContour(id, payload);
    },

    getVersions: async () => {
      return EspFirmwareService.getApiEspFirmwaresVersions();
    },

    getHistory: async (id: string) => {
      return EspFirmwareService.getApiEspFirmwaresHistory(id);
    },

    download: async (id: string) => {
      return downloadArtifactFile(`/api/esp-firmwares/${encodeURIComponent(id)}/download`);
    },
  },
};
